import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { useEffect, useState, useCallback, useContext } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { BiX } from "react-icons/bi";
import "../css/RoomModal.css";
import AuthContext from "../../../context/AuthProvider";
import { useTranslation } from "react-i18next";

export default function ParentModal({
  open,
  onClose,
  data = {},
  onParentSaved,
  mode = "add",
}) {
  const axiosPrivate = useAxiosPrivate();
  const { school } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const [form, setForm] = useState({
    firstName: "", 
    lastName: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });

  const [selectFields, setSelectFields] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // Add mode starts with an email check (same as mobile): "email" -> "existing" | "form"
  const [step, setStep] = useState(mode === "edit" ? "form" : "email");
  const [existingParent, setExistingParent] = useState(null);
  const [checkStatus, setCheckStatus] = useState(null);

  const resetForm = useCallback(() => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      notes: "",
    });
    setSelectFields({});
    setErrors({});
    setStep(mode === "edit" ? "form" : "email");
    setExistingParent(null);
    setCheckStatus(null);
  }, [mode]);

  const handleCheckEmail = async () => {
    const email = form.email.trim();
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: t("parent_modal.errors.emailRequired") });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const res = await axiosPrivate.get(API_URLS.checkNurseryParentEmail(school.id, email));
      const status = res.data?.status;

      if (status === "not_found") {
        setStep("form");
      } else if (status === "found" || status === "already_in_this_nursery") {
        setExistingParent(res.data.parent);
        setCheckStatus(status);
        setStep("existing");
      } else if (status === "invalid_type") {
        setErrors({
          email: isAr
            ? "هذا البريد مرتبط بحساب ليس ولي أمر."
            : "This email belongs to an account that is not a parent.",
        });
      } else {
        setErrors({ server: t("parent_modal.errors.generic") });
      }
    } catch (error) {
      setErrors({ server: error.response?.data?.detail || t("parent_modal.errors.generic") });
    } finally {
      setIsLoading(false);
    }
  };

  // Parent from another nursery gets linked to this one first; one already here is just selected
  const handleUseExistingParent = async () => {
    if (!existingParent?.id) return;

    setIsLoading(true);
    try {
      if (checkStatus === "found") {
        await axiosPrivate.post(API_URLS.linkParentToNursery(school.id), {
          parent_id: existingParent.id,
        });
      }
      onParentSaved?.(existingParent.id, existingParent);
      handleClose();
    } catch (error) {
      setErrors({ server: error.response?.data?.detail || t("parent_modal.errors.generic") });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSelectChange = (fieldKey) => (e) => {
    setSelectFields((prev) => ({ ...prev, [fieldKey]: e.target.value }));
  };

  const validateForm = () => {
    const newErrors = {};

   if (!form.firstName.trim()) {
      newErrors.firstName = t("parent_modal.errors.firstNameRequired");
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = t("parent_modal.errors.lastNameRequired");
    }
    if (!form.email.trim()) {
      newErrors.email = t("parent_modal.errors.emailRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const payload = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        notes: form.notes.trim(),
        // The API expects `nurseries` as an array of nursery IDs
        nurseries: [school.id],
        ...selectFields,
      };

      let res;
      if (mode === "edit" && data?.parent?.id) {
        res = await axiosPrivate.put(
          `${API_URLS.updateParent(school.id, data.parent.id)}`,
          payload
        );
        onParentSaved?.(data.parent.id, payload);
      } else {
        res = await axiosPrivate.post(API_URLS.addParent(school.id), payload);
        onParentSaved?.(res.data.id, payload);
      }

      handleClose();
    } catch (error) {
      if (
        error.response?.status === 400 &&
        typeof error.response.data === "object"
      ) {
        const fieldErrors = {};
        for (const key in error.response.data) {
          if (Array.isArray(error.response.data[key])) {
            fieldErrors[key.toLowerCase()] = error.response.data[key][0];
          }
        }
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      } else {
        setErrors((prev) => ({
          ...prev,
          server: t("parent_modal.errors.generic"),
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Prefill data in edit mode
  useEffect(() => {
    if (open) {
      if (mode === "edit" && data?.parent?.id) {
        setIsLoading(true);

        axiosPrivate
          .get(`${API_URLS.getParent(school.id, data.parent.id)}`)
          .then((res) => {
            const parent = res.data;
            setForm({
              firstName: parent.first_name || "",
              lastName: parent.last_name || "",
              email: parent.email || "",
              phone: parent.phone || "",
              address: parent.address || "",
              notes: parent.notes || "",
            });

            if (Array.isArray(data.fields)) {
              const initialSelects = {};
              data.fields.forEach((f) => {
                initialSelects[f.key] = f.value || "";
              });
              setSelectFields(initialSelects);
            }
          })
          .catch((err) => {
            console.error("Error fetching parent details:", err);
            setErrors((prev) => ({
              ...prev,
              server: t("parent_modal.errors.generic"),
            }));
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        resetForm();
      }
    }
  }, [open, mode, data, resetForm, axiosPrivate, t]);

  const selectFieldData =
    mode === "edit" ? data.fields || [] : Array.isArray(data) ? data : [];

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h5" fontWeight="bold" color="secondary">
            {mode === "edit"
              ? t("parent_modal.editTitle")
              : t("parent_modal.title")}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

        {step === "email" && (
          <>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {isAr
                ? "أدخل بريد ولي الأمر الإلكتروني أولاً للتحقق مما إذا كان مسجلاً بالفعل."
                : "Enter the parent's email first to check whether they are already registered."}
            </Typography>
            <Box mb={3}>
              <Typography variant="body1" color="secondary" gutterBottom>
                {t("parent_modal.fields.email")} <span className="text-danger">*</span>
              </Typography>
              <input
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                onKeyDown={(e) => e.key === "Enter" && !isLoading && handleCheckEmail()}
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                placeholder={t("parent_modal.placeholders.email")}
                disabled={isLoading}
                autoFocus
              />
              {errors.email && <Typography className="errorText">{errors.email}</Typography>}
            </Box>
            {errors.server && (
              <Typography className="errorText" mb={2}>
                {errors.server}
              </Typography>
            )}
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleCheckEmail}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                className="submitButton"
              >
                {isAr ? "تحقق من البريد" : "Check Email"}
              </Button>
            </Box>
          </>
        )}

        {step === "existing" && existingParent && (
          <>
            <Typography variant="h6" color="secondary" mb={1}>
              {checkStatus === "found"
                ? isAr
                  ? "ولي الأمر مسجل في مدرسة أخرى"
                  : "Parent exists in another school"
                : isAr
                ? "ولي الأمر مسجل بالفعل في هذه المدرسة"
                : "Parent is already in this school"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {checkStatus === "found"
                ? isAr
                  ? "سيتم ربط ولي الأمر بهذه المدرسة وإضافته للطالب."
                  : "The parent will be linked to this school and added to the student."
                : isAr
                ? "سيتم إضافة ولي الأمر للطالب."
                : "The parent will be added to the student."}
            </Typography>
            <Box mb={3} p={2} sx={{ background: "#f5eef9", borderRadius: "8px" }}>
              <Typography fontWeight={600}>
                {`${existingParent.first_name ?? ""} ${existingParent.last_name ?? ""}`.trim()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {existingParent.email}
              </Typography>
              {existingParent.phone && (
                <Typography variant="body2" color="text.secondary">
                  {existingParent.phone}
                </Typography>
              )}
            </Box>
            {errors.server && (
              <Typography className="errorText" mb={2}>
                {errors.server}
              </Typography>
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Button
                onClick={() => {
                  setStep("email");
                  setExistingParent(null);
                  setErrors({});
                }}
                disabled={isLoading}
                sx={{ textTransform: "none", color: "#702E8F" }}
              >
                {isAr ? "تحقق من بريد آخر" : "Check another email"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUseExistingParent}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                className="submitButton"
              >
                {checkStatus === "found"
                  ? isAr
                    ? "ربط وإضافة"
                    : "Link & Add"
                  : t("actions.add")}
              </Button>
            </Box>
          </>
        )}

        {step === "form" && (
        <>
        {[
          { label: "First Name", field: "firstName", type: "text", required: true },
          { label: "Last Name", field: "lastName", type: "text", required: true },
          { label: "Email", field: "email", type: "email", required: true },
          { label: "Phone Number", field: "phone", type: "text" },
          { label: "Address", field: "address", type: "text" },
        ].map(({ label, field, type, required }) => (
          <Box mb={3} key={field}>
            <Typography variant="body1" color="secondary" gutterBottom>
              {t(`parent_modal.fields.${field}`)}{" "}
              {required && <span className="text-danger">*</span>}
            </Typography>
            <input
              type={type}
              value={form[field]}
              onChange={handleChange(field)}
              className={`form-control ${errors[field] ? "is-invalid" : ""}`}
              placeholder={t(`parent_modal.placeholders.${field}`)}
              // In add mode the email was already checked in the first step
              disabled={isLoading || (mode !== "edit" && field === "email")}
            />
            {errors[field] && (
              <Typography className="errorText">{errors[field]}</Typography>
            )}
          </Box>
        ))}

        {selectFieldData.map((field) => (
          <Box mb={3} key={field.label}>
            <Typography variant="body1" color="secondary" gutterBottom>
             {t(`parent_modal.fields.${field.label.toLowerCase()}`, { defaultValue: field.label })}
            </Typography>
            <Select
              fullWidth
              displayEmpty
              value={selectFields[field.key] || ""}
              onChange={handleSelectChange(field.key)}
              disabled={isLoading}
              sx={{
                height: "40px",
                "& .MuiSelect-select": {
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              }}
            >
              <MenuItem value="" disabled>
                --- {t("actions.select")} ---
              </MenuItem>
              {field.options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {field.key === "countries" 
                  ? t(`countries.${opt.id}`, { defaultValue: opt.name }) 
                  : 
                  field.key === "Nationality" || field.label === "Nationality"
                  ? (i18n.language === "ar" ? (opt.Name_ar || opt.name) : opt.name)
                  : 
                  t(`options.${opt.name}`, { defaultValue: opt.name })
                }
              </MenuItem>
            ))}
            </Select>
          </Box>
        ))}

        <Box mb={3}>
          <Typography variant="body1" color="secondary" gutterBottom>
            {t("parent_modal.fields.notes")}
          </Typography>
          <textarea
            rows={4}
            value={form.notes}
            onChange={handleChange("notes")}
            className="form-control"
            placeholder={t("parent_modal.placeholders.notes")}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ced4da",
              fontFamily: "inherit",
              fontSize: "inherit",
              minHeight: "100px",
            }}
          />
        </Box>

        {errors.server && (
          <Typography className="errorText" mb={2}>
            {errors.server}
          </Typography>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
            className="submitButton"
          >
            {isLoading
              ? t("room_modal.processing")
              : mode === "edit"
              ? t("actions.edit")
              : t("actions.add")}
          </Button>
        </Box>
        </>
        )}
      </Box>
    </Modal>
  );
}